import { useState, useEffect, useRef } from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface NavigationSection {
  id: string;
  label: string;
  icon?: LucideIcon;
}

interface PageNavigationProps {
  sections: NavigationSection[];
  className?: string;
}

export const PageNavigation = ({ sections, className }: PageNavigationProps) => {
  const [activeSection, setActiveSection] = useState<string>(sections[0]?.id || '');
  const [isVisible, setIsVisible] = useState(false);
  const sectionPositionsRef = useRef<Map<string, { top: number; height: number }>>(new Map());

  // Cache section positions on mount and resize
  useEffect(() => {
    const cacheSectionPositions = () => {
      const positions = new Map<string, { top: number; height: number }>();
      sections.forEach(section => {
        const element = document.getElementById(section.id);
        if (element) {
          positions.set(section.id, {
            top: element.offsetTop,
            height: element.offsetHeight
          });
        }
      });
      sectionPositionsRef.current = positions;
    };

    cacheSectionPositions();
    window.addEventListener('resize', cacheSectionPositions);
    return () => window.removeEventListener('resize', cacheSectionPositions);
  }, [sections]);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          
          // Show navigation after scrolling past hero
          setIsVisible(scrollY > 400);

          // Update active section based on cached positions
          const scrollPosition = scrollY + 150;

          for (const section of sections) {
            const position = sectionPositionsRef.current.get(section.id);
            if (position) {
              const { top, height } = position;
              if (scrollPosition >= top && scrollPosition < top + height) {
                setActiveSection(section.id);
                break;
              }
            }
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100; // Account for fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <nav
      className={cn(
        "fixed left-6 top-1/2 -translate-y-1/2 z-40 transition-all duration-300 hidden lg:block",
        isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10 pointer-events-none",
        className
      )}
      aria-label="Page navigation"
    >
      <div className="bg-card/80 backdrop-blur-sm rounded-full border border-border shadow-warm p-2">
        <ul className="space-y-2">
          {sections.map((section) => (
            <li key={section.id}>
              <button
                onClick={() => scrollToSection(section.id)}
                className={cn(
                  "group relative flex items-center gap-3 px-4 py-2.5 rounded-full transition-all duration-200",
                  activeSection === section.id
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-muted text-muted-foreground hover:text-foreground"
                )}
                aria-current={activeSection === section.id ? 'true' : undefined}
              >
                {/* Icon or Dot */}
                <span className="flex-shrink-0">
                  {section.icon ? (
                    <section.icon className="w-5 h-5" strokeWidth={1.5} />
                  ) : (
                    <span className="text-lg">•</span>
                  )}
                </span>
                
                {/* Label - shows on hover */}
                <span className={cn(
                  "absolute left-full ml-4 whitespace-nowrap bg-card/95 backdrop-blur-sm px-3 py-1.5 rounded-md border border-border shadow-warm text-sm font-medium transition-all duration-200",
                  "opacity-0 -translate-x-2 pointer-events-none group-hover:opacity-100 group-hover:translate-x-0"
                )}>
                  {section.label}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};
